import { BudgetPhases } from 'business/constants';
import * as Sentry from '@sentry/browser';
import { omit, keys } from 'lodash';
import {
    BUDGET_YEAR_FIELD,
    EVOLUTION_N_N_1,
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    VOTED_FIELD,
    REPORTED_FIELD,
} from 'routes/BudgetModule/config/constants';
import {
    getAmountFieldForPhase,
    isActiveBudgetPhase,
} from 'routes/BudgetModule/shared/phases/helpers';

type EvolutionT = {
    totalAmountRequested: number;
    totalAmountVoted?: number;
};

// TODO: improve typing, this is just an alias for ResponseDataT, tracked by Epic "Kill les ResponseDataT dans Budget" (Shortcut ID: 26370)
type BudgetLineData = Omit<ResponseDataT, 'filter'>;
type BudgetLineWithEvolutionT = BudgetLineData & {
    evolution: EvolutionT;
};

const getBlKeyForVentilationColumnsAndYear =
    (ventilationColumns: string[]) =>
    (bl: BudgetLineData, year?: number | null) => {
        const ventilatedColValues = ventilationColumns
            .map((col) => bl[col] || '')
            .join('--');
        const yearValue = year || bl[BUDGET_YEAR_FIELD];
        return `${yearValue}--${ventilatedColValues}}`;
    };

type BudgetLineByYearAndKey = { [budgetLineUniqueKey: string]: BudgetLineData };
const mapBudgetLinesByKey = (
    data: BudgetLineData[],
    blKeyGetter: (bl: BudgetLineData) => string,
) =>
    data.reduce<BudgetLineByYearAndKey>((acc, bl) => {
        const blKey = blKeyGetter(bl);
        if (acc[blKey]) {
            Sentry.captureException(
                new Error(
                    'Several budget_lines found for the evolution column, we should check what is going on',
                ),
            );
            return acc;
        }
        acc[blKey] = bl;
        return acc;
    }, {});

/**
 * Return new budget lines that we must create in order to take into account previous child-less BL.
 *
 * This is quite tricky: when a budget line of the past year does not exist anymore in the
 * selected year (it's child-less), the past voted amount is not taken into account when
 * computing the evolution (because no line exist in the current year, hence we never look
 * for this previous voted amount when going through the current year budget lines).
 *
 * To fix this issue, we find the child-less lines of the previous year and we create a
 * new line in the current year with the same dimensions but with only a value for the
 * evolution column ! That way the voted amount of the last year is taken into account
 * when computing the evolution.
 */
const createMissingBudgetLinesWithEvolutionValue = (
    budgetPhase: BudgetPhases,
    selectedYear: number,
    data: BudgetLineData[],
    getBudgetLineKey: (budgetLine: BudgetLineData, year: number) => string,
    blByYearAndSid: BudgetLineByYearAndKey,
): BudgetLineWithEvolutionT[] => {
    if (!isActiveBudgetPhase(budgetPhase)) {
        console.error(
            `this phase requires an update of this helpers:` +
                ' which amount should we filter out when creating the new budget lines?',
        );
    }

    const isLastYear = (budgetLine: BudgetLineData) =>
        budgetLine[BUDGET_YEAR_FIELD] === selectedYear - 1;

    const isOrphan = (budgetLine: BudgetLineData) => {
        const blKey = getBudgetLineKey(budgetLine, selectedYear);
        const currentYearBudgetLine = blByYearAndSid[blKey];
        const isAnOrphean = !currentYearBudgetLine;
        return isAnOrphean;
    };

    const generateNewBudgetLine = (
        lastYearOrphean: BudgetLineData,
    ): BudgetLineWithEvolutionT => {
        const totalAmountVoted = lastYearOrphean[VOTED_FIELD];

        // we want the new budget line to only be taken into account
        // for the evolution column, so it must not have amounts, otherwise
        // the data shown would be false (last year euros would be injected
        // in the current year budget)
        const keysToFilterOut = [
            SERVICE_PROPOSAL_AMOUNT,
            SIMULATED_ARBITRATED_AMOUNT,
            PRIMITIVE_BUDGET_AMOUNT,
            SUPPLEMENTARY_BUDGET_AMOUNT,
            REPORTED_FIELD,
            // NB when adding a new phase, we want to filter out its amount here
            ...keys(lastYearOrphean).filter((key) =>
                key.startsWith('budgetLine.'),
            ),
        ];
        const newBudgetLine = omit(
            lastYearOrphean,
            keysToFilterOut,
        ) as BudgetLineWithEvolutionT;
        newBudgetLine[BUDGET_YEAR_FIELD] = selectedYear;
        newBudgetLine[EVOLUTION_N_N_1] = { totalAmountVoted } as EvolutionT;
        return newBudgetLine;
    };

    return data
        .filter((bl) => isLastYear(bl) && isOrphan(bl))
        .map(generateNewBudgetLine);
};

const getAddEvolutionValueToBl =
    (
        budgetPhase: BudgetPhases,
        selectedYear: number,
        getBudgetLineKey: (budgetLine: BudgetLineData, year: number) => string,
        budgetLineMap: BudgetLineByYearAndKey,
    ) =>
    (budgetLine: BudgetLineData): BudgetLineData | BudgetLineWithEvolutionT => {
        // NB when adding a new phase, we want to use its amount here
        const totalAmountRequestedField = getAmountFieldForPhase(budgetPhase);

        if (budgetLine[BUDGET_YEAR_FIELD] !== selectedYear) return budgetLine;

        const totalAmountRequested = budgetLine[
            totalAmountRequestedField
        ] as number;

        const evolutionDataPoint: EvolutionT = {
            totalAmountRequested,
        };

        const previousBudgetLineKey = getBudgetLineKey(
            budgetLine,
            selectedYear - 1,
        );
        const previousBudgetLine = budgetLineMap[previousBudgetLineKey];

        if (previousBudgetLine) {
            // NB we do not sum sum here as we are at the budget line level
            const totalAmountVoted = previousBudgetLine[VOTED_FIELD] as number;
            evolutionDataPoint.totalAmountVoted = totalAmountVoted;
        }
        const budgetLineWithEvolution = {
            ...budgetLine,
            [EVOLUTION_N_N_1]: evolutionDataPoint,
        } as BudgetLineWithEvolutionT;
        return budgetLineWithEvolution;
    };

// TODO: most of this helper could be handled more efficiently by the backend OR in AgGrid valueGetter, with calculated columns
/**
 * Return a new list of point with a new columns to compute the budget evolution.
 *
 * The budget evolution is define by (arbitre N  -  voted N-1) / voted N-1  for the "arbitrage phase",
 * or by (requested N  -  voted N-1) / voted  for the "saisie phase".
 */
export const addEvolutionColumnValues = (
    budgetPhase: BudgetPhases,
    selectedYear: number,
    data: BudgetLineData[],
    ventilationColumns: string[],
): (BudgetLineWithEvolutionT | BudgetLineData)[] => {
    if (!isActiveBudgetPhase(budgetPhase)) {
        console.error(
            `this phase requires an update of this helpers:` +
                ' which amount should we use for the totalAmountRequestedField ?',
        );
    }
    const getBudgetLineKey =
        getBlKeyForVentilationColumnsAndYear(ventilationColumns);
    const budgetLineMap = mapBudgetLinesByKey(data, getBudgetLineKey);

    const addEvolutionValue = getAddEvolutionValueToBl(
        budgetPhase,
        selectedYear,
        getBudgetLineKey,
        budgetLineMap,
    );

    const budgetLinesToAdd = createMissingBudgetLinesWithEvolutionValue(
        budgetPhase,
        selectedYear,
        data,
        getBudgetLineKey,
        budgetLineMap,
    );

    return data.map(addEvolutionValue).concat(budgetLinesToAdd);
};
